import { MODUL_PROJEKTI, type LetoPodatkov, type Podjetje as PodjetjeTip, type PorociloNaVoljo } from '@koda/shared';
import { useCallback, useEffect, useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../lib/api';
import { useBesedila, useJezik } from '../lib/jezik';
import { useUporabnik } from '../lib/uporabnik';
import { IzbrisPodjetja } from '../sestavni/IzbrisPodjetja';
import { skupineZavihkov } from '../sestavni/Glava';
import { NastavitevPaketa } from '../sestavni/NastavitevPaketa';
import { PreimenujPodjetje } from '../sestavni/PreimenujPodjetje';

/**
 * Pregled podjetja: izbira leta in poročila.
 *
 * Naenkrat sta na voljo največ dve leti: zadnje (tekoče) in eno primerjalno.
 * Primerjalno je privzeto prejšnje, zamenjati ga je mogoče s katerim koli
 * starejšim — starejša leta so v bazi, v pregledu pa se nikoli ne pokažeta
 * več kot dve (docs/ARHITEKTURA.md, 6.1).
 */
export function Podjetje(): JSX.Element {
  const { id = '' } = useParams();
  const uporabnik = useUporabnik();
  const b = useBesedila();
  const jezik = useJezik();
  const [podjetje, setPodjetje] = useState<PodjetjeTip | null>(null);
  const [leta, setLeta] = useState<LetoPodatkov[] | null>(null);
  const [primerjalno, setPrimerjalno] = useState<number | null>(null);
  const [izbranoLeto, setIzbranoLeto] = useState<number | null>(null);
  const [porocila, setPorocila] = useState<PorociloNaVoljo[]>([]);
  const [napaka, setNapaka] = useState<string | null>(null);
  // Povezava na odstotke projektov je samo pri podjetju z vklopljenim modulom (15.3).
  const [moduli, setModuli] = useState<string[]>([]);

  // Katera porocila podjetje ima, pove paket; v kodi tega seznama ni.
  const osveziPorocila = useCallback((): void => {
    api
      .porocilaNaVoljo(id, jezik)
      .then(setPorocila)
      .catch(() => {
        setPorocila([]);
      });
  }, [id, jezik]);

  useEffect(() => {
    api
      .podjetja()
      .then((vsa) => {
        setPodjetje(vsa.find((p) => p.id === id) ?? null);
      })
      .catch(() => {
        setPodjetje(null);
      });
    api
      .leta(id)
      .then((l) => {
        setLeta(l);
        setIzbranoLeto(l[0]?.leto ?? null);
        setPrimerjalno(l[1]?.leto ?? null);
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : b.letNiMogoce);
      });
    osveziPorocila();
    api
      .moduli(id)
      .then(setModuli)
      .catch(() => {
        setModuli([]);
      });
  }, [id, osveziPorocila]);

  const vsa = leta ?? [];
  const tekoce = vsa[0]?.leto ?? null;
  const starejsa = vsa.slice(1);
  const prikazana = vsa.filter((l) => l.leto === tekoce || l.leto === primerjalno);

  const zamenjajPrimerjalno = (leto: number): void => {
    setPrimerjalno(leto);
    // Ce je bilo prav primerjalno leto izbrano, izbira sledi zamenjavi.
    if (izbranoLeto !== tekoce) setIzbranoLeto(leto);
  };

  return (
    <main>
      <h1>{podjetje?.naziv ?? b.podjetje}</h1>

      {napaka !== null && <p className="napaka">{napaka}</p>}

      <section className="kartica">
        <h2>{b.poslovnoLeto}</h2>
        {leta === null && <p>{b.berem}</p>}
        {leta !== null && leta.length === 0 && (
          <p>
            {b.brezKnjizb}
            {uporabnik.jeAdmin && (
              <>
                {' '}
                <Link to={`/podjetje/${id}/uvoz`}>{b.naloziIzvoz}</Link>.
              </>
            )}
          </p>
        )}
        {prikazana.length > 0 && (
          <>
            <div className="leta">
              {prikazana.map((l) => (
                <button
                  key={l.leto}
                  type="button"
                  className={l.leto === izbranoLeto ? 'leto izbrano' : 'leto'}
                  onClick={() => {
                    setIzbranoLeto(l.leto);
                  }}
                >
                  {l.leto}
                  <span className="drobno">
                    {l.knjizb.toLocaleString(jezik === 'en' ? 'en-GB' : 'sl-SI')} {b.knjizb}
                  </span>
                </button>
              ))}
            </div>
            {starejsa.length > 1 && (
              <label className="drobno primerjalno">
                {b.primerjalnoLeto}
                <select
                  value={String(primerjalno ?? '')}
                  onChange={(d) => {
                    zamenjajPrimerjalno(Number.parseInt(d.target.value, 10));
                  }}
                >
                  {starejsa.map((l) => (
                    <option key={l.leto} value={l.leto}>
                      {l.leto}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {starejsa.length > 0 && (
              <p className="namig">{b.namigLeta}</p>
            )}
          </>
        )}
      </section>

      {izbranoLeto !== null && (
        <section className="kartica">
          <h2>{b.porocilaZa(izbranoLeto)}</h2>
          {porocila.length === 0 && (
            <p>
              {b.brezPorocil}
              {uporabnik.jeAdmin && b.paketSpodaj}
            </p>
          )}
          {/* Razvrščeno po glavnih zavihkih menija (6.6), z istimi nazivi. */}
          {skupineZavihkov(porocila).map((s) => (
            <div key={s.koda} className="skupina-porocil">
              {porocila.some((p) => p.skupina !== null && p.skupina !== undefined) && <h3>{s.naziv}</h3>}
              <ul className="seznam">
                {s.porocila.map((p) => (
                  <li key={p.vrsta}>
                    <Link to={`/podjetje/${id}/porocilo/${p.vrsta}?leto=${String(izbranoLeto)}`}>
                      {p.naziv}
                    </Link>
                    <span className="drobno">{b.opisiPorocil[p.vrsta] ?? ''}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}

      {moduli.includes(MODUL_PROJEKTI) && (
        <section className="kartica">
          <h2>{b.projekti}</h2>
          <p>
            <Link to={`/podjetje/${id}/projekti/pregled`}>{b.projektiPregled}</Link>
            <span className="namig"> {b.namigProjektiPregled}</span>
          </p>
          <p>
            <Link to={`/podjetje/${id}/projekti/odstotki`}>{b.nastavitevOdstotkov}</Link>
            <span className="namig"> {b.namigNastavitevOdstotkov}</span>
          </p>
          {uporabnik.jeAdmin && (
            <p>
              <Link to={`/podjetje/${id}/projekti/nastavitve`}>{b.nastavitveProjektov}</Link>
              <span className="namig"> {b.namigNastavitveProjektov}</span>
            </p>
          )}
        </section>
      )}

      {uporabnik.jeAdmin && <NastavitevPaketa podjetje={id} naSpremembo={osveziPorocila} />}

      {uporabnik.jeAdmin && (
        <section className="kartica">
          <h2>{b.podatki}</h2>
          <p>
            <Link to={`/podjetje/${id}/uvoz`}>{b.naloziIzvoz}</Link>
          </p>
          <p>
            <Link to={`/podjetje/${id}/nastavitve`}>{b.nastavitveRazvrscanja}</Link>
            <span className="namig"> {b.namigNastavitve}</span>
          </p>
          <p>
            <Link to={`/podjetje/${id}/nastavitve/kazalniki`}>{b.nastavitveKazalnikov}</Link>
            <span className="namig"> {b.namigNastavitveKazalnikov}</span>
          </p>
          <p>
            <Link to={`/podjetje/${id}/nastavitve/fcvc`}>{b.nastavitveFcVc}</Link>
            <span className="namig"> {b.namigNastavitveFcVc}</span>
          </p>
          <p>
            <Link to={`/podjetje/${id}/nastavitve/partnerji`}>{b.nastavitvePartnerjev}</Link>
            <span className="namig"> {b.namigNastavitvePartnerjev}</span>
          </p>
        </section>
      )}

      {uporabnik.jeAdmin && podjetje !== null && (
        <PreimenujPodjetje
          podjetje={id}
          naziv={podjetje.naziv}
          naSpremembo={(naziv) => {
            setPodjetje({ ...podjetje, naziv });
          }}
        />
      )}

      {uporabnik.jeAdmin && podjetje !== null && (
        <IzbrisPodjetja podjetje={id} naziv={podjetje.naziv} />
      )}
    </main>
  );
}
